import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { referralsAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Crown, Check, X, Sparkles, Brain, Heart, Shield, Leaf,
  ArrowRight, Star, Lock, Zap, Users, Share2, Copy, MessageCircle,
} from 'lucide-react';

const PREMIUM_FEATURES = [
  { icon: Sparkles, title: 'Sementes Personalizadas com IA', desc: 'Gere sementes únicas baseadas na sua jornada de recuperação' },
  { icon: Leaf, title: 'Evolução do Jardim & NFTs', desc: 'Plantas evoluem com seu progresso real — desbloqueie estágios exclusivos' },
  { icon: Brain, title: 'Insights e Padrões', desc: 'Dashboard inteligente mostrando sua evolução emocional ao longo do tempo' },
  { icon: Shield, title: '10 Atividades Diárias', desc: 'Bloco avançado de transformação profunda (FREE tem apenas 5)' },
  { icon: Heart, title: 'Diário Profundo', desc: 'Escrita terapêutica ilimitada com privacidade total' },
  { icon: Star, title: 'Badge Premium', desc: 'Destaque na comunidade + prioridade em reuniões e grupos' },
];

const COMPARISON = [
  { feature: 'Feed Social', free: true, premium: true },
  { feature: 'Check-in Diário', free: true, premium: true },
  { feature: 'Grupos de Apoio', free: true, premium: true },
  { feature: 'Atividades Diárias', free: '5/dia', premium: '10/dia' },
  { feature: 'Jardim da Alma', free: 'Básico', premium: 'Completo + IA' },
  { feature: 'Sementes com IA', free: false, premium: true },
  { feature: 'Insights e Padrões', free: false, premium: true },
  { feature: 'Diário Profundo', free: false, premium: true },
  { feature: 'Badge Premium', free: false, premium: true },
  { feature: 'Reuniões Prioritárias', free: false, premium: true },
  { feature: 'Cursos e Tratamentos', free: false, premium: 'Em breve' },
];

function ComparisonCell({ value }) {
  if (value === true) return <Check className="h-4 w-4 text-green-400 mx-auto" />;
  if (value === false) return <X className="h-4 w-4 text-gray-600 mx-auto" />;
  return <span className="text-xs text-center block">{value}</span>;
}

function InviteSection({ user }) {
  const [referralLink, setReferralLink] = useState('');
  const [totalUses, setTotalUses] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) loadReferral();
  }, [user]);

  const loadReferral = async () => {
    setLoading(true);
    try {
      const res = await referralsAPI.getMyLink();
      setReferralLink(res.data.referral_link);
      setTotalUses(res.data.total_uses || 0);
    } catch { /* silently fail for unauthenticated */ }
    finally { setLoading(false); }
  };

  const shareMessage = `Estou usando o U-Clean para melhorar minha vida e minha recuperação.\nSe quiser entrar comigo, usa meu link: ${referralLink}`;

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, '_blank');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      toast.success('Link copiado!');
    });
  };

  if (!user || !referralLink) return null;

  return (
    <section className="px-4 pb-16 max-w-2xl mx-auto" data-testid="invite-section">
      <div className="rounded-xl p-6" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)' }}>
        <div className="text-center mb-4">
          <Share2 className="h-8 w-8 text-green-400 mx-auto mb-2" />
          <h3 className="text-lg font-bold text-white">Convide Amigos & Ganhe</h3>
          <p className="text-sm text-gray-400">Cada cadastro = <span className="text-green-400 font-semibold">1.000 AWA</span> · Premium = <span className="text-green-400 font-semibold">40% em AWA Sacável (R$)</span></p>
          {totalUses > 0 && (
            <p className="text-xs text-green-400 mt-1">{totalUses} {totalUses === 1 ? 'pessoa entrou' : 'pessoas entraram'} com seu link</p>
          )}
        </div>

        {/* Link display */}
        <div className="flex items-center gap-2 mb-4 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <input
            readOnly
            value={referralLink}
            className="flex-1 bg-transparent text-sm text-gray-300 outline-none truncate"
            data-testid="referral-link-input"
          />
          <button onClick={handleCopy} className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="copy-link-btn">
            <Copy className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        {/* WhatsApp button */}
        <button
          onClick={handleWhatsApp}
          className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 transition-all hover:brightness-110"
          style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
          data-testid="whatsapp-invite-btn"
        >
          <MessageCircle className="h-5 w-5" />
          Convidar pelo WhatsApp
        </button>
      </div>
    </section>
  );
}

export default function PremiumPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremium = user?.is_premium || user?.is_founder;

  const goToCheckout = () => navigate('/checkout');

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #06061a 0%, #0f0a2a 30%, #0c0c2d 60%, #0a0a1a 100%)' }} data-testid="premium-page">
      {/* Hero */}
      <section className="relative px-4 pt-16 pb-20 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(circle at 50% 30%, rgba(139,92,246,0.3), transparent 60%)' }} />
        <div className="relative max-w-2xl mx-auto">
          {isPremium && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-xs font-medium" style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }} data-testid="premium-active-badge">
              <Check className="h-3 w-3" /> Seu Premium está ativo
            </motion.div>
          )}

          {/* Badge Plano Fundador */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05 }} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full mb-5 text-xs font-bold tracking-wide uppercase" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.2), rgba(217,119,6,0.1))', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)' }} data-testid="founder-badge">
            <Crown className="h-3.5 w-3.5" /> Plano Fundador
          </motion.div>

          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1, type: 'spring' }} className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8b5cf6, #f59e0b)' }}>
            <Crown className="h-10 w-10 text-white" />
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Transforme sua recuperação com o <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #a78bfa, #f59e0b)' }}>Premium</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-base sm:text-lg text-gray-400 mb-8 max-w-lg mx-auto">
            Acesse ferramentas avançadas de autoconhecimento, sementes personalizadas com IA e acompanhe sua evolução como nunca antes.
          </motion.p>

          {!isPremium && (
            <>
              <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={goToCheckout} className="px-8 py-4 rounded-xl text-base font-bold text-white inline-flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', boxShadow: '0 8px 32px rgba(139,92,246,0.3)' }} data-testid="hero-cta">
                Ativar Premium <ArrowRight className="h-5 w-5" />
              </motion.button>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.75 }} className="text-xs text-gray-500 mt-3">
                Acesso imediato · Sem fidelidade · Cancele quando quiser
              </motion.p>
            </>
          )}

          {/* Social proof */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="mt-8 flex items-center justify-center gap-2" data-testid="social-proof">
            <div className="flex -space-x-2">
              {[...'ABCDE'].map((l, i) => (
                <div key={i} className="w-7 h-7 rounded-full border-2 border-slate-900 flex items-center justify-center text-[10px] font-bold text-white" style={{ background: ['#8b5cf6','#f59e0b','#ec4899','#3b82f6','#10b981'][i] }}>{l}</div>
              ))}
            </div>
            <p className="text-xs text-gray-500">Mais de <span className="text-purple-400 font-semibold">1.200 pessoas</span> já começaram sua jornada</p>
          </motion.div>
        </div>
      </section>

      {/* Features grid */}
      <section className="px-4 pb-16 max-w-4xl mx-auto">
        <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-8">O que o Premium libera</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PREMIUM_FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * i }} className="rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: 'rgba(139,92,246,0.15)' }}>
                  <Icon className="h-5 w-5 text-purple-400" />
                </div>
                <h3 className="text-sm font-bold text-white mb-1">{f.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Comparison table */}
      <section className="px-4 pb-16 max-w-2xl mx-auto">
        <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-8">Gratuito vs Premium</h2>
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="grid grid-cols-3 text-center py-3 text-xs font-bold" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <span className="text-gray-400">Recurso</span>
            <span className="text-gray-400">Gratuito</span>
            <span className="text-purple-400">Premium</span>
          </div>
          {COMPARISON.map((row, i) => (
            <div key={i} className="grid grid-cols-3 items-center py-3 px-3 text-sm" style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
              <span className="text-gray-300 text-xs">{row.feature}</span>
              <ComparisonCell value={row.free} />
              <ComparisonCell value={row.premium} />
            </div>
          ))}
        </div>
      </section>

      {/* Coming soon */}
      <section className="px-4 pb-16 max-w-2xl mx-auto">
        <div className="rounded-xl p-6 text-center" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <Zap className="h-8 w-8 text-amber-400 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-white mb-2">Em construção para Premium</h3>
          <p className="text-sm text-gray-400 mb-4">Cursos profissionais, tratamentos de 90 e 180 dias, e programa para familiares estão em fase final de desenvolvimento e serão exclusivos para assinantes Premium.</p>
          <div className="flex flex-wrap justify-center gap-2">
            {['Curso PAREI-AD', 'Tratamento 90 dias', 'Tratamento 180 dias', 'Programa Familiar'].map((c, i) => (
              <span key={i} className="text-xs px-3 py-1 rounded-full" style={{ background: 'rgba(245,158,11,0.1)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.2)' }}>
                <Lock className="h-3 w-3 inline mr-1" />{c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Invite Friends */}
      <InviteSection user={user} />

      {/* Final CTA */}
      {!isPremium && (
        <section className="px-4 pb-20 text-center">
          <h2 className="text-xl font-bold text-white mb-3">Pronto para transformar sua jornada?</h2>
          <p className="text-sm text-gray-500 mb-6">A partir de R$ 29,90/mês</p>
          <button onClick={goToCheckout} className="px-8 py-4 rounded-xl text-base font-bold text-white inline-flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', boxShadow: '0 8px 32px rgba(139,92,246,0.3)' }} data-testid="final-cta">
            Ativar Premium Agora <Crown className="h-5 w-5" />
          </button>
          <p className="text-xs text-gray-600 mt-3">Acesso imediato · Sem fidelidade · Cancele quando quiser</p>
        </section>
      )}
    </div>
  );
}
